import React, { useState } from 'react';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Plus, Search, Users, Loader2 } from 'lucide-react';
import { AnimatePresence } from 'framer-motion';
import MemberCard from './components/members/MemberCard';
import MemberModal from './components/members/MemberModal';
import ConfirmDialog from './components/shared/ConfirmDialog';
import { mockMembers } from './components/data/mockData';

export default function Members() {
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [isLoading] = useState(false);
  
  const [members, setMembers] = useState(mockMembers);

  const handleSave = (memberData) => {
    if (editingMember) {
      setMembers(members.map(m => m.id === editingMember.id ? { ...memberData, id: editingMember.id } : m));
    } else {
      const newMember = {
        ...memberData,
        id: `${Date.now()}`,
        created_date: new Date().toISOString()
      };
      setMembers([...members, newMember]);
    }
  };

  const handleEdit = (member) => {
    setEditingMember(member);
    setIsModalOpen(true);
  };

  const handleDelete = (id) => {
    const member = members.find(m => m.id === id);
    setDeleteConfirm({ id, name: member?.name || 'este membro' });
  };

  const confirmDelete = () => {
    if (deleteConfirm) {
      setMembers(members.filter(m => m.id !== deleteConfirm.id));
    }
  };

  const handleAddNew = () => {
    setEditingMember(null);
    setIsModalOpen(true);
  };

  // Filter members by search query
  const filteredMembers = members.filter(member => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      member.name?.toLowerCase().includes(query) ||
      member.profession?.toLowerCase().includes(query) ||
      member.email?.toLowerCase().includes(query)
    );
  });

  const activeMembers = filteredMembers.filter(m => m.active).length;
  const totalMembers = members.length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-cyan-50/30">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-lg border-b border-slate-200/50 sticky top-0 z-10 shadow-sm">
        <div className="px-4 md:px-6 py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            {/* Logo and Title */}
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center shadow-lg shadow-purple-200 ring-4 ring-purple-100">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
                  Membros da Equipe
                </h1>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                    <p className="text-sm text-slate-600 font-medium">
                      {activeMembers} ativos de {totalMembers} membros
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3">
              {/* Search */}
              <div className="relative flex-1 md:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Buscar membros..."
                  className="pl-9 bg-white/80 border-slate-200 shadow-sm focus:shadow-md focus:border-purple-300 transition-all"
                />
              </div>

              {/* Add Member */}
              <Button 
                className="bg-gradient-to-r from-blue-600 to-blue-600 hover:from-blue-700 hover:to-blue-700 shrink-0 shadow-lg shadow-purple-200 hover:shadow-xl hover:scale-105 transition-all font-semibold text-white"
                onClick={handleAddNew}
              >
                <Plus className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Novo Membro</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="p-4 md:p-6">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
          </div>
        ) : filteredMembers.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-400">
            <div className="w-20 h-20 rounded-3xl bg-slate-100 flex items-center justify-center mb-4">
              <Users className="w-10 h-10" />
            </div>
            <p className="text-lg font-medium">
              {searchQuery ? 'Nenhum membro encontrado' : 'Nenhum membro cadastrado'}
            </p>
            <p className="text-sm mt-1">
              {searchQuery ? 'Tente buscar com outros termos' : 'Adicione o primeiro membro da sua equipe'}
            </p>
            {!searchQuery && (
              <Button 
                className="mt-6 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 shadow-lg shadow-purple-200"
                onClick={handleAddNew}
              >
                <Plus className="w-4 h-4 mr-2" />
                Adicionar Primeiro Membro
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <AnimatePresence>
              {filteredMembers.map((member) => (
                <MemberCard
                  key={member.id}
                  member={member}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              ))}
            </AnimatePresence>
          </div>
        )}
      </main>

      {/* Member Modal */}
      <MemberModal
        member={editingMember}
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingMember(null);
        }}
        onSave={handleSave}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={confirmDelete}
        title="Excluir Membro"
        description={`Tem certeza que deseja excluir ${deleteConfirm?.name}? Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        variant="danger"
      />
    </div>
  );
}